import { Box, Button, DialogActions, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import FormField from 'components/common/formikForm/formField';
import FormWrapper from 'components/common/formikForm/formWrapper';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import api from 'api';
import { clientServiceProviderKeys } from 'hooks/useClientServiceProviders';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { OrganizationOnboardingFormInterface } from 'types';
import { isFeatureNotEnabledError } from 'utils/features';
import {
    organizationOnboardingFormInitialValues,
    organizationFormValidationSchema,
} from 'utils/validations/onboarding';

interface Props {
    onClose: () => void;
    topContent?: ReactNode;
}

const OrganizationForm = ({ onClose, topContent }: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { translate } = useTranslate();
    const [disabled, setDisabled] = useState(false);
    const sectionLabelSx = {
        color: '#64748b',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        mb: 1.25,
    };
    const formAreaSx = {
        '& .MuiTextField-root, & .MuiFormControl-root': {
            width: '100%',
        },
        '& .MuiInputLabel-root': {
            color: '#64748b',
            fontWeight: 500,
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '14px',
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            boxShadow: '0 1px 0 rgba(255, 255, 255, 0.85)',
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
            '& fieldset': {
                borderColor: 'rgba(148, 163, 184, 0.28)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(131, 83, 226, 0.38)',
            },
            '&.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(131, 83, 226, 0.08)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#8353e2',
            },
        },
        '& .MuiFormHelperText-root': {
            marginLeft: '4px',
        },
        '& .MuiFormControlLabel-root': {
            margin: 0,
            minHeight: '42px',
            alignItems: 'center',
        },
        '& .MuiCheckbox-root.Mui-checked, & .MuiRadio-root.Mui-checked': {
            color: '#8353e2',
        },
    };

    const onboardingTypeOptions = useMemo(
        () => [
            { label: translate('selfOnboarding'), value: 'selfOnboarding' },
            { label: translate('manualIntake'), value: 'manualIntake' },
        ],
        [translate]
    );

    const handleSubmit = async (values: OrganizationOnboardingFormInterface) => {
        try {
            setDisabled(true);
            const { onboardingType, ...payload } = values;

            const response = await api.clientServiceProviders.onboardClient(payload);
            if (response.status === 200 || response.status === 201) {
                const companyId = response.data.service_provider.id;
                dispatch(
                    setAlert({
                        message: translate('clientSuccessfullyAdded'),
                        type: 'success',
                    })
                );
                void queryClient.invalidateQueries({ queryKey: clientServiceProviderKeys.all });
                onClose();
                setTimeout(() => {
                    if (values.onboardingType === 'manualIntake') {
                        navigate(`/dashboard/${companyId}/organization`);
                    }
                }, 2000);
            }
        } catch (error) {
            if (isFeatureNotEnabledError(error)) {
                return;
            }
            dispatch(setAlert({ message: translate('somethingWentWrong'), type: 'error' }));
        } finally {
            setDisabled(false);
        }
    };

    return (
        <FormWrapper
            initialValues={organizationOnboardingFormInitialValues}
            validationSchema={organizationFormValidationSchema(translate)}
            onSubmit={handleSubmit}
        >
            <Box
                sx={{
                    px: { xs: 2.25, sm: 3 },
                    pt: 2,
                    pb: 0,
                    ...formAreaSx,
                }}
            >
                <Stack spacing={1.5}>
                    {topContent && <Box>{topContent}</Box>}
                    <Box>
                        {/* <Typography sx={sectionLabelSx}>{translate('organizationName')}</Typography> */}
                        <FormField
                            required
                            size="small"
                            name="company_name"
                            label="organizationName"
                            disabled={disabled}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: 1.5,
                        }}
                    >
                        <Box>
                            {/* <Typography sx={sectionLabelSx}>{translate('contactPersonFirstName')}</Typography> */}
                            <FormField
                                required
                                size="small"
                                name="firstname"
                                label="contactPersonFirstName"
                                disabled={disabled}
                            />
                        </Box>
                        <Box>
                            {/* <Typography sx={sectionLabelSx}>{translate('contactPersonLastName')}</Typography> */}
                            <FormField
                                required
                                size="small"
                                name="lastname"
                                label="contactPersonLastName"
                                disabled={disabled}
                            />
                        </Box>
                    </Box>
                    <Box>
                        {/* <Typography sx={sectionLabelSx}>
                            {translate('contactPersonEmail')}
                        </Typography> */}
                        <FormField
                            required
                            size="small"
                            name="contact_email"
                            label="contactPersonEmail"
                            disabled={disabled}
                        />
                    </Box>
                    <Box>
                        <Typography sx={sectionLabelSx}>{translate('onboardingType')}</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <FormField
                                type="radio"
                                size="small"
                                name="onboardingType"
                                label=""
                                options={onboardingTypeOptions}
                                disabled={disabled}
                            />
                        </Stack>
                    </Box>
                </Stack>
            </Box>
            <DialogActions
                sx={{
                    px: { xs: 2.25, sm: 3 },
                    py: 2.5,
                    mt: 1.5,
                    borderTop: '1px solid rgba(148, 163, 184, 0.18)',
                    background: 'rgba(255, 255, 255, 0.72)',
                    backdropFilter: 'blur(12px)',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                }}
            >
                <Button
                    type="button"
                    onClick={onClose}
                    disabled={disabled}
                    sx={{
                        height: 40,
                        px: 2.25,
                        borderRadius: '12px',
                        border: '1px solid rgba(148, 163, 184, 0.35)',
                        color: '#475569',
                        backgroundColor: '#ffffff',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                            borderColor: 'rgba(131, 83, 226, 0.28)',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 10px 22px rgba(15, 23, 42, 0.08)',
                        },
                    }}
                >
                    {translate('cancel')}
                </Button>
                <SubmitButton
                    loading={disabled}
                    sx={{
                        height: 40,
                        px: 2.25,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #8353e2 0%, #6d41d8 100%)',
                        boxShadow: '0 12px 26px rgba(131, 83, 226, 0.28)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #8353e2 0%, #6d41d8 100%)',
                            boxShadow: '0 16px 30px rgba(131, 83, 226, 0.32)',
                        },
                    }}
                >
                    save&Proceed
                </SubmitButton>
            </DialogActions>
        </FormWrapper>
    );
};

export default OrganizationForm;
